import { REGEX_TYPES } from 'Enum/RegexTypes';
import { regexFormController } from 'Utils/regexFormController';
import { ReactNode, createContext, useState } from 'react';

const defaultErrors = {
  firstName: null,
  lastName: null,
  IsrId: null,
  phonePrefix: null,
  phoneNumber: null,
  email: null,
  token: null,
  refreshToken: null,
};

export interface IUserOTPLoginDetails {
  firstName: string;
  lastName: string;
  IsrId: string;
  phonePrefix: string;
  phoneNumber: string;
  email: string;
  token: string | null;
  refreshToken: string | null;
  [key: string]: string | null;
}
interface IErrors {
  firstName: string | null;
  lastName: string | null;
  IsrId: string | null;
  phonePrefix: string | null;
  phoneNumber: string | null;
  email: string | null;
}
interface IOTPLoginContext {
  step: number;
  setStep: Function;
  userDetails: IUserOTPLoginDetails;
  setUserDetails: Function;
  isFormValidated: Function;
  errors: IErrors;
  setErrors: (errors: IErrors) => void;
}

export const OTPLoginContext = createContext<IOTPLoginContext>({
  step: 0,
  setStep: () => {},
  userDetails: {
    firstName: '',
    lastName: '',
    IsrId: '',
    phonePrefix: '',
    phoneNumber: '',
    email: '',
    token: null,
    refreshToken: null,
  },
  setUserDetails: () => {},
  isFormValidated: () => {},
  errors: defaultErrors,
  setErrors: () => {},
});

type Props = { children: ReactNode };

export const OTPLoginContextProvider = ({ children }: Props) => {
  const [step, setStep] = useState<number>(0);
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    IsrId: '',
    phonePrefix: '',
    phoneNumber: '',
    email: '',
    token: null,
    refreshToken: null,
  });

  const [errors, setErrors] = useState<IErrors>(defaultErrors);

  const currentErrors: IErrors = { ...errors };

  const isFormValidated = () => {
    const validations = [
      regexFormController(
        REGEX_TYPES.NAME,
        userDetails.firstName,
        (error: string | null) => {
          setErrors((prevErrors) => ({ ...prevErrors, firstName: error }));
          currentErrors.firstName = error;
        }
      ),
      regexFormController(
        REGEX_TYPES.NAME,
        userDetails.lastName,
        (error: string | null) => {
          setErrors((prevErrors) => ({ ...prevErrors, lastName: error }));
          currentErrors.lastName = error;
        }
      ),
      regexFormController(
        REGEX_TYPES.ID,
        userDetails.IsrId,
        (error: string | null) => {
          setErrors((prevErrors) => ({ ...prevErrors, IsrId: error }));
          currentErrors.IsrId = error;
        }
      ),
      regexFormController(
        REGEX_TYPES.PHONE,
        userDetails.phoneNumber,
        (error: string | null) => {
          setErrors((prevErrors) => ({ ...prevErrors, phoneNumber: error }));
          currentErrors.phoneNumber = error;
        }
      ),
      userDetails.email.trim() === '' ||
        regexFormController(
          REGEX_TYPES.EMAIL,
          userDetails.email,
          (error: string | null) => {
            setErrors((prevErrors) => ({ ...prevErrors, email: error }));
            currentErrors.email = error;
          }
        ),
      userDetails.phonePrefix !== '',
    ];

    const errorsObject: { [key: string]: string | null } = { ...currentErrors };

    return {
      isValid: !validations.includes(false),
      firstErrorName: Object.keys(currentErrors).find(
        (field: string) => errorsObject[field]
      ),
    };
  };

  return (
    <OTPLoginContext.Provider
      value={{
        step,
        setStep,
        userDetails,
        setUserDetails,
        isFormValidated,
        errors,
        setErrors,
      }}
    >
      {children}
    </OTPLoginContext.Provider>
  );
};
